// Sustainability types (QP-179) — shared between web and api.
//
// A portfolio collection may define a sustainability configuration: one SFDR
// scale criterion, up to two extra "bar" scale criteria, and any number of
// boolean "product preference" criteria. Scale criteria (SFDR + bars) carry an
// ordered list of LABELED ordinal steps (min 2, max 7) — the array order is the
// ordinal order, lowest→highest. Products carry a step key per scale criterion
// (and a boolean per product preference); the client states a minimum-required
// step in the `sustainability` flow component; the matching service resolves
// step keys to their index within the scale's ordered steps and evaluates each
// goal portfolio's products against the preferences
// (productIndex >= requiredIndex), freezing the result in a per-portfolio
// "sustainability" snapshot. Deviations require an advisor decision (accept /
// do not proceed) tied to the evaluated state via a basis hash.

import { getLocalizedValue, type LocalizedString } from "../i18n";
import type { SnapshotInvalidation } from "./flow-instances";

// ── Collection config ────────────────────────────────────────────────────────

/** Min/max ordered steps per scale criterion (enforced server-side + in the UI). */
export const SUSTAINABILITY_MIN_STEPS = 2;
export const SUSTAINABILITY_MAX_STEPS = 7;
/** QL-414 — a bar's short table-header label: long enough for a name, too short for a question. */
export const SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH = 40;

/**
 * The three EU SFDR article types (Morningstar `eusfdrType`), in ascending
 * ordinal order (6 < 8 < 9). A product's effective SFDR score is resolved from
 * its linked instrument's `eusfdrType` (or a product-level override); the client
 * states a minimum-required score and matching compares ordinally.
 */
export const SFDR_SCORE_VALUES = [6, 8, 9] as const;
export type SfdrScoreValue = (typeof SFDR_SCORE_VALUES)[number];

/** One of the fixed SFDR article scores — a fixed `value` with an editable label. */
export type SustainabilitySfdrScore = {
  /** Fixed article number 6/8/9 — read-only. */
  value: SfdrScoreValue;
  labelI18n: LocalizedString;
};

/**
 * The 16 canonical ESG sector keys (camelCase). Two Morningstar-derived boolean
 * maps live in `timeseries_meta.meta` keyed by these:
 *   - `esgExclusions[sector]` — the fund STATES it excludes the sector
 *     (EET `SectorBasedExclusions.{Sector}.Exclusion === "Y"`). SPARSE — a key is
 *     present only when the EET reports it (absent = not reported).
 *   - `esgInvolvement[sector]` — the fund HAS exposure to the sector
 *     (`ESG.ProductInvolvement.{Sector} > 0`). Complete — all 16 present.
 * A product preference criterion may map to one of these sectors + a resolution
 * mode.
 */
export const SUSTAINABILITY_EXCLUSION_KEYS = [
  "cannabis",
  "smallArms",
  "tobacco",
  "fossil",
  "nuclear",
  "gambling",
  "adultEntertainment",
  "animalTesting",
  "furAndSpecialtyLeather",
  "palmOil",
  "pesticides",
  "thermalCoal",
  "contentiousWeapons",
  "militaryContracting",
  "gmos",
  "alcohol",
] as const;
export type SustainabilityExclusionKey = (typeof SUSTAINABILITY_EXCLUSION_KEYS)[number];

/**
 * How a mapped product preference resolves the product's effective value from
 * the instrument:
 *   - `excludes`     → the fund STATES it excludes the sector (esgExclusions[s] === true).
 *   - `not_involved` → the fund has NO exposure to the sector (esgInvolvement[s] === false).
 *   - `either`       → excludes OR not-involved (fills the sparse-exclusion gap; default).
 */
export const SUSTAINABILITY_EXCLUSION_MODES = ["excludes", "not_involved", "either"] as const;
export type SustainabilityExclusionMode = (typeof SUSTAINABILITY_EXCLUSION_MODES)[number];

/** Default resolution mode when a sector is first mapped to a product preference. */
export const DEFAULT_SUSTAINABILITY_EXCLUSION_MODE: SustainabilityExclusionMode = "either";

/**
 * A product's linked instrument's raw Morningstar ESG data, read from
 * `timeseries_meta.meta`. Drives the LIVE resolution of SFDR (from `eusfdrType`)
 * and mapped product preferences (from the two boolean maps), and backs the
 * read-only "Sustainability" diagnostic tab.
 */
export type InstrumentEsgMeta = {
  /** EU SFDR article (6/8/9), or null when the instrument has none. */
  eusfdrType: SfdrScoreValue | null;
  /**
   * Sector → the fund STATES it excludes it. SPARSE: a key is present only when
   * the EET reports that sector (absent = not reported).
   */
  esgExclusions: Partial<Record<SustainabilityExclusionKey, boolean>>;
  /**
   * Sector → the fund HAS exposure to it (`ProductInvolvement > 0`). Complete —
   * all reported sectors present.
   */
  esgInvolvement: Partial<Record<SustainabilityExclusionKey, boolean>>;
};

/**
 * Resolve a mapped product preference's effective value from an instrument's
 * ESG maps per mode (the single source of truth shared by the matching service
 * and the web UI). Returns undefined when the instrument reports NEITHER signal
 * for the sector.
 *   - excludes     → esgExclusions[sector] === true (stated policy; sparse)
 *   - not_involved → esgInvolvement[sector] === false (no exposure)
 *   - either       → excludes OR not-involved (OR semantics; fills the gap)
 */
export function resolveProductPreferenceFromInstrument(
  exclusionKey: SustainabilityExclusionKey,
  mode: SustainabilityExclusionMode,
  instrument: Pick<InstrumentEsgMeta, "esgExclusions" | "esgInvolvement">,
): boolean | undefined {
  const excl = instrument.esgExclusions?.[exclusionKey];
  const inv = instrument.esgInvolvement?.[exclusionKey];
  if (mode === "excludes") return excl === true;
  if (mode === "not_involved") return typeof inv === "boolean" ? inv === false : undefined;
  if (excl === undefined && inv === undefined) return undefined;
  return excl === true || inv === false;
}

/**
 * One labeled ordinal step of a scale criterion. `key` is an internal
 * auto-generated identifier (crypto.randomUUID()), never user-facing. Steps are
 * ORDERED — array position is the ordinal (index 0 = lowest).
 */
export type SustainabilityScaleStep = {
  key: string;
  labelI18n: LocalizedString;
};

/** A keyed criterion (bar or product preference) with an admin-entered label. */
export type SustainabilityCriterion = {
  /** Stable slug, unique within its group. */
  key: string;
  labelI18n: LocalizedString;
};

/**
 * A product preference criterion — a keyed label, an optional icon, and an
 * optional mapping to one of the 16 canonical sectors + a resolution mode. When
 * `exclusionKey` is set the product's preference value live-resolves from the
 * instrument's Morningstar ESG maps per `exclusionMode`; when absent the
 * product preference is a manual boolean on the product.
 */
export type SustainabilityProductPreferenceCriterion = SustainabilityCriterion & {
  /** Raw SVG markup for the product preference's icon (Lucide picker / upload), or absent. */
  iconSvg?: string;
  /** One of the 16 sector keys, or absent for a manual product preference. */
  exclusionKey?: SustainabilityExclusionKey;
  /** How to resolve the mapped sector (default "either"); ignored when unmapped. */
  exclusionMode?: SustainabilityExclusionMode;
};

/** A scale criterion (bar) — a keyed label plus its ordered labeled steps. */
export type SustainabilityScaleCriterion = SustainabilityCriterion & {
  /**
   * QL-414 — a short name for table headers ("EU Taxonomy"). The label is the
   * full question the client answers, which does not fit a column head. Absent
   * = the label is used.
   */
  shortLabelI18n?: LocalizedString;
  /** Ordered labeled steps (min 2, max 7); index = ordinal, lowest→highest. */
  steps: SustainabilityScaleStep[];
};

/**
 * `portfolio_collection.sustainabilityConfig`. Null/absent = sustainability is
 * disabled for the collection (everything downstream hidden/inert).
 */
export type CollectionSustainabilityConfig = {
  /**
   * SFDR criterion — a general label plus EXACTLY 3 fixed-score labels
   * (values 6/8/9). Null when SFDR is not used. Driven by Morningstar
   * `eusfdrType`; not a free step scale.
   */
  sfdr: { labelI18n: LocalizedString; scores: SustainabilitySfdrScore[] } | null;
  /** 0-2 additional scale criteria with ordered steps (max 2 enforced server-side). */
  bars: SustainabilityScaleCriterion[];
  /** 0-N boolean product preference criteria (optionally mapped to an exclusion + icon). */
  productPreferences: SustainabilityProductPreferenceCriterion[];
  /**
   * Which alignment modes this collection works with (QL-110). Absent or empty
   * = both allowed, i.e. the client picks (today's behaviour). Exactly one =
   * pinned: the client is not asked and the matching engine applies that mode
   * regardless of the stored answer. Read only through
   * `allowedAlignmentModes` / `effectiveAlignmentMode`.
   */
  alignmentModes?: SustainabilityAlignmentMode[];
  /**
   * Offer an explicit "No preference" option in the product-preference section
   * (QL-142). Absent/false = today's behaviour. Dormant while the collection has
   * zero product preferences. Read only through `resolveProductPreferenceAnswer`.
   */
  productPreferencesNoPreferenceEnabled?: boolean;
  /**
   * How the product-preference section is rendered in the flow (QL-142):
   * `checkbox` = today's list, `card` = icon-card grid. Absent = checkbox. Read
   * only through `effectiveProductPreferencesDisplay`.
   */
  productPreferencesDisplay?: SustainabilityProductPreferencesDisplay;
};

// ── Product values ───────────────────────────────────────────────────────────

/**
 * `product.sustainability` — the product's EXPLICIT (override/manual) value per
 * configured criterion. SFDR + mapped product preferences are otherwise
 * LIVE-RESOLVED from the linked instrument's Morningstar data (`eusfdrType` /
 * ESG sector maps); only an explicit override or a manual value (unlinked
 * instrument / unmapped product preference) is stored here.
 */
export type ProductSustainability = {
  /**
   * Explicit SFDR score (6/8/9). An override of the instrument's `eusfdrType`,
   * or a manual value when the instrument has none. Unset = use the instrument's
   * eusfdrType (or fail when it has none).
   */
  sfdrOverride?: SfdrScoreValue;
  /** bar key → step key. */
  bars?: Record<string, string>;
  /**
   * product preference key → boolean. For a MAPPED product preference this
   * overrides the instrument's exclusion boolean; for an UNMAPPED product
   * preference it is the manual value.
   */
  productPreferences?: Record<string, boolean>;
};

// ── Client preferences (flow component `sustainability`) ─────────────────────

/**
 * How a portfolio is judged against the client's preferences:
 *   - `individual` → every instrument must meet them;
 *   - `portfolio`  → a weighted share of the portfolio must (see `thresholdPct`).
 * The array order is canonical and is what `normalizeAlignmentModes` sorts to.
 */
export const SUSTAINABILITY_ALIGNMENT_MODES = ["individual", "portfolio"] as const;
export type SustainabilityAlignmentMode = (typeof SUSTAINABILITY_ALIGNMENT_MODES)[number];

export type SustainabilityPreferences = {
  /** Minimum-required SFDR score (6/8/9), when the criterion is configured. */
  sfdr?: SfdrScoreValue | null;
  /** bar key → minimum-required step key. */
  bars?: Record<string, string | null>;
  /** product preference key → the client wants it (default false). */
  productPreferences?: Record<string, boolean>;
  /** Individual: every instrument must meet; Portfolio: a weighted share must. */
  alignmentMode?: SustainabilityAlignmentMode | null;
  /**
   * One of `SUSTAINABILITY_THRESHOLD_OPTIONS`, required when alignmentMode ===
   * "portfolio". Typed as a plain number because stored blobs predate the list.
   */
  thresholdPct?: number | null;
  /**
   * The client explicitly declared "No preference" for product preferences
   * (QL-142). Its own field — never a key inside `productPreferences`. Read only
   * through `resolveProductPreferenceAnswer`; a concrete theme wins over it.
   */
  noProductPreference?: boolean;
};

// ── Alignment modes (QL-110) ─────────────────────────────────────────────────
//
// The collection declares which alignment modes it works with; the client's
// saved answer only matters when the collection allows both. These three pure
// functions are the ONLY place that rule exists — the settings UI, the flow
// component, the matching engine, the completion engine and the PDF renderer
// all go through them rather than reading `alignmentModes` directly.

/**
 * Clean a raw `alignmentModes` value: unknown entries dropped, duplicates
 * merged, order canonicalised to `SUSTAINABILITY_ALIGNMENT_MODES`. An empty
 * result means "both allowed" — never "no mode at all".
 */
export function normalizeAlignmentModes(raw: unknown): SustainabilityAlignmentMode[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<SustainabilityAlignmentMode>();
  for (const value of raw) {
    const mode = SUSTAINABILITY_ALIGNMENT_MODES.find((m) => m === value);
    if (mode) seen.add(mode);
  }
  return SUSTAINABILITY_ALIGNMENT_MODES.filter((mode) => seen.has(mode));
}

/** The modes the collection permits. Absent or empty ⇒ both. */
export function allowedAlignmentModes(
  config: Pick<CollectionSustainabilityConfig, "alignmentModes"> | null | undefined,
): SustainabilityAlignmentMode[] {
  const modes = normalizeAlignmentModes(config?.alignmentModes);
  return modes.length > 0 ? modes : [...SUSTAINABILITY_ALIGNMENT_MODES];
}

/**
 * The mode that actually applies to a portfolio: exactly one allowed ⇒ that
 * mode, whatever the client saved; otherwise the saved answer when it is one of
 * the allowed modes, else null (not yet answered).
 */
export function effectiveAlignmentMode(
  config: Pick<CollectionSustainabilityConfig, "alignmentModes"> | null | undefined,
  preferences: Pick<SustainabilityPreferences, "alignmentMode"> | null | undefined,
): SustainabilityAlignmentMode | null {
  const allowed = allowedAlignmentModes(config);
  if (allowed.length === 1) return allowed[0] ?? null;
  const saved = preferences?.alignmentMode;
  return saved != null && allowed.includes(saved) ? saved : null;
}

// ── Portfolio threshold (QL-696) ─────────────────────────────────────────────
//
// In `portfolio` mode the client picks the minimum share from a fixed list
// instead of typing a number. The widget offers exactly these options and the
// server normalizer keeps only these values, so both read the list from here.

export const SUSTAINABILITY_THRESHOLD_OPTIONS = [5, 10, 20, 30, 40, 50, 60, 80] as const;
export type SustainabilityThresholdPct = (typeof SUSTAINABILITY_THRESHOLD_OPTIONS)[number];

/**
 * A raw threshold resolved to one of `SUSTAINABILITY_THRESHOLD_OPTIONS`, else
 * null. Numeric strings are accepted. A value outside the list (for example a
 * free-form 25 saved before QL-696) reads as unanswered.
 */
export function normalizeThresholdPct(value: unknown): SustainabilityThresholdPct | null {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  if (typeof n !== "number") return null;
  return SUSTAINABILITY_THRESHOLD_OPTIONS.find((option) => option === n) ?? null;
}

// ── No product preference (QL-142) ──────────────────────────────────────────
//
// The collection may offer an explicit "No preference" answer in the product-
// preference section, which then becomes a required question. This single pure
// function owns the rule — the normaliser, the widget, the completion engine
// and the PDF renderer all read through it and never inspect the raw flag.

export type ProductPreferenceAnswer = {
  /** Toggle on AND ≥1 product preference configured. */
  required: boolean;
  /** Effective "No preference": required, flag true, and no theme selected. */
  noPreference: boolean;
  /** Not required, or `noPreference`, or ≥1 theme selected. */
  answered: boolean;
};

export function resolveProductPreferenceAnswer(
  // Structural on purpose: the rule only reads preference KEYS, so the
  // completion engine can pass its slim config shape alongside the full
  // `CollectionSustainabilityConfig`.
  config:
    | {
        productPreferences?: ReadonlyArray<{ key: string }>;
        productPreferencesNoPreferenceEnabled?: boolean;
      }
    | null
    | undefined,
  preferences:
    | Pick<SustainabilityPreferences, "productPreferences" | "noProductPreference">
    | null
    | undefined,
): ProductPreferenceAnswer {
  const configured = config?.productPreferences ?? [];
  const required = config?.productPreferencesNoPreferenceEnabled === true && configured.length > 0;
  // Only configured keys count — unknown keys are dropped by the normaliser anyway.
  const anyTheme = configured.some((pref) => preferences?.productPreferences?.[pref.key] === true);
  const noPreference = required && preferences?.noProductPreference === true && !anyTheme;
  return { required, noPreference, answered: !required || noPreference || anyTheme };
}

// ── Product-preference display mode (QL-142) ────────────────────────────────

/** Rendering of the product-preference section: checkbox list or icon-card grid. */
export const SUSTAINABILITY_PRODUCT_PREFERENCES_DISPLAYS = ["checkbox", "card"] as const;
export type SustainabilityProductPreferencesDisplay =
  (typeof SUSTAINABILITY_PRODUCT_PREFERENCES_DISPLAYS)[number];

/** Clean a raw display value: one of the two literals, else undefined (= absent). */
export function normalizeProductPreferencesDisplay(
  raw: unknown,
): SustainabilityProductPreferencesDisplay | undefined {
  return SUSTAINABILITY_PRODUCT_PREFERENCES_DISPLAYS.find((mode) => mode === raw);
}

/** The display mode that applies. Absent/unknown ⇒ `checkbox`. */
export function effectiveProductPreferencesDisplay(
  config: Pick<CollectionSustainabilityConfig, "productPreferencesDisplay"> | null | undefined,
): SustainabilityProductPreferencesDisplay {
  return normalizeProductPreferencesDisplay(config?.productPreferencesDisplay) ?? "checkbox";
}

/** Raw user input persisted as the component's inputData. */
export type SustainabilityComponentInput = {
  /** The gate: "Is sustainability important?" Null = unanswered. */
  important: boolean | null;
  /** Present only while important === true (cleared server-side otherwise). */
  preferences?: SustainabilityPreferences | null;
};

/** Server-normalized calculatedData for the component. */
export type SustainabilityComponentResult = {
  important: boolean | null;
  /** Normalized preferences; null when important !== true. */
  preferences: SustainabilityPreferences | null;
  lastUpdatedAt: string;
};

// ── Advisor deviation decision (goal_portfolio.sustainabilityDecision) ───────

export type SustainabilityDecisionValue = "accepted" | "do_not_proceed";

export type GoalSustainabilityDecision = {
  decision: SustainabilityDecisionValue;
  justification?: string;
  decidedAt: string;
  /** Hash of the evaluated state the decision was made against. */
  basisHash: string;
};

// ── Matching results (snapshot type "sustainability", per portfolio) ─────────

/**
 * The client's required value for a criterion column. Scales carry the required
 * step's label + its ordinal index within the scale (so the table/PDF can print
 * the label, not a bare number); product preferences carry `true`.
 */
export type SustainabilityRequiredValue =
  | { kind: "scale"; stepKey: string; index: number; labelI18n: LocalizedString }
  | { kind: "sfdr"; value: SfdrScoreValue; labelI18n: LocalizedString }
  | { kind: "product_preference"; value: true };

/** One evaluated criterion column of the matching table. */
export type SustainabilityCriterionDescriptor = {
  /** "sfdr" | `bar:${key}` | `product_preference:${key}` — stable column id. */
  id: string;
  kind: "sfdr" | "bar" | "product_preference";
  /** bar/product preference key; null for sfdr. */
  key: string | null;
  labelI18n: LocalizedString;
  /** QL-414 — a bar's short table-header name, frozen with the snapshot; absent = use the label. */
  shortLabelI18n?: LocalizedString;
  /** Product preference icon (raw SVG markup), frozen into the snapshot so the table/PDF can render it. */
  iconSvg?: string;
  /** The client's required value (scale step label+index, or true for product preferences). */
  required: SustainabilityRequiredValue;
};

/**
 * The product's value for a criterion column. Scales carry the product's step
 * label + its ordinal index (or null when missing/unknown); product preferences
 * carry the boolean (or null when unset).
 */
export type SustainabilityActualValue =
  | { kind: "scale"; stepKey: string; index: number; labelI18n: LocalizedString }
  | {
      kind: "sfdr";
      value: SfdrScoreValue;
      labelI18n: LocalizedString;
      /** True when the value came from the instrument's eusfdrType (not an override/manual). */
      fromInstrument: boolean;
    }
  | { kind: "product_preference"; value: boolean; fromInstrument?: boolean }
  | null;

export type SustainabilityCriterionResult = {
  /** Matches a `SustainabilityCriterionDescriptor.id`. */
  criterionId: string;
  /** The product's value; null = missing/unknown (fails the criterion). */
  actual: SustainabilityActualValue;
  meets: boolean;
};

export type SustainabilityProductEvaluation = {
  productId: string;
  name: string;
  isin: string | null;
  /** Weight in the portfolio, percent. */
  weight: number;
  meets: boolean;
  perCriterion: SustainabilityCriterionResult[];
};

export type SustainabilityMatchStatus = "ok" | "deviation";

/**
 * Match status + advisor decision → what the goal effectively is:
 * ok (matches), accepted (deviation accepted), not_accepted (do not proceed),
 * deviation (no valid decision yet).
 */
export type SustainabilityEffectiveStatus = "ok" | "accepted" | "not_accepted" | "deviation";

/** Frozen per-portfolio matching result (flow_instance_snapshot "sustainability"). */
export type SustainabilitySnapshotData = {
  mode: SustainabilityAlignmentMode;
  /** Set when mode === "portfolio". */
  thresholdPct: number | null;
  criteria: SustainabilityCriterionDescriptor[];
  products: SustainabilityProductEvaluation[];
  /** Sum of weights (pct) of products that meet the preferences. */
  weightMeetingPct: number;
  status: SustainabilityMatchStatus;
  /** Hash of (preferences, composition, product values) for THIS portfolio. */
  basisHash: string;
  timestamp: string;
};

// ── Portfolio state (widget + completion) ────────────────────────────────────

/**
 * The sustainability state of ONE portfolio (QP-179 — the status component is
 * portfolio-scoped, rendered once per portfolio on the portfolio page). The
 * deviation decision lives on `goal_portfolio.sustainabilityDecision` and is
 * tied to the portfolio's own basis hash.
 */
export type PortfolioSustainabilityStateResponse = {
  portfolioId: string;
  portfolioName: string;
  /** False when the collection has no config or preferences say important ≠ yes. */
  enabled: boolean;
  /** The portfolio's match status (null when not evaluable — no snapshot yet). */
  status: SustainabilityMatchStatus | null;
  effective: SustainabilityEffectiveStatus | null;
  /** The stored decision when its basisHash is still valid, else null. */
  decision: GoalSustainabilityDecision | null;
  /** The portfolio's current basis hash (null when not evaluable). */
  basisHash: string | null;
  /** The frozen per-portfolio matching snapshot (null until evaluable). */
  snapshot: SustainabilitySnapshotData | null;
};

export type SaveSustainabilityDecisionRequest = {
  /** Null clears the decision. */
  decision: SustainabilityDecisionValue | null;
  justification?: string;
};

export type SaveSustainabilityDecisionResponse = {
  success: boolean;
  error?: string;
  state?: PortfolioSustainabilityStateResponse;
};

// ── Settings endpoints ───────────────────────────────────────────────────────

export type SustainabilityConfigResponse = {
  config: CollectionSustainabilityConfig | null;
};

export type UpdateSustainabilityConfigRequest = {
  /** Null disables sustainability for the collection. */
  config: CollectionSustainabilityConfig | null;
};

// ── Component save cascade ───────────────────────────────────────────────────

/** Outcome of the recompute cascade after a sustainability component save. */
export type SustainabilitySyncResult = {
  changed: boolean;
  refreshHints: SnapshotInvalidation[];
};

/**
 * QL-414 — a criterion's short table-header label in exactly this language, or
 * "" when it has none there. No fallback to another language: the header then
 * falls back to the full label, which is translated, so a short label typed in
 * one language never heads a column of a document in another.
 */
export function sustainabilityShortLabel(
  shortLabelI18n: LocalizedString | null | undefined,
  locale: string,
): string {
  return (shortLabelI18n as Record<string, string | undefined> | null | undefined)?.[locale] ?? "";
}

/**
 * QL-657 — one of the client's criteria as a short phrase in `locale`: the
 * scale's name and the minimum the client asked for ("SFDR ≥ Article 8"), or
 * the product preference's label.
 */
export function sustainabilityCriterionText(
  criterion: SustainabilityCriterionDescriptor,
  locale: string,
): string {
  const { required } = criterion;
  if (required.kind === "product_preference") {
    return getLocalizedValue(criterion.labelI18n, locale);
  }
  const name =
    criterion.kind === "sfdr"
      ? "SFDR"
      : sustainabilityShortLabel(criterion.shortLabelI18n, locale) ||
        getLocalizedValue(criterion.labelI18n, locale);
  return `${name} ≥ ${getLocalizedValue(required.labelI18n, locale)}`;
}

/**
 * QL-414 — the config with everything that is presentation only removed: today
 * the bars' short table-header labels. Two configs equal under this function
 * evaluate every portfolio identically and pick the same products, so a save
 * that differs only here must not void decisions or rebuild portfolios.
 */
export function sustainabilityMatchingConfig(
  config: CollectionSustainabilityConfig | null,
): CollectionSustainabilityConfig | null {
  if (!config) return null;
  return {
    ...config,
    bars: (config.bars ?? []).map(({ shortLabelI18n: _shortLabelI18n, ...bar }) => bar),
  };
}
